import dayjs, { OpUnitType } from 'dayjs';
import { ReactElement, useEffect, useState } from 'react';
import { Event, EventProps, View, Views, ViewsProps } from 'react-big-calendar';
import BaseCalendar from '~/components/common/base-calendar/BaseCalendar';
import styles from './Calendar.module.scss';
import classNames from 'classnames/bind';
import { EVENT_COLOR_DEFAULT } from '~/utils/constants/schedules';
import { Box } from '@mui/material';
import { BaseImage, BaseScrollbar } from '~/components/common';
import React from 'react';
import { IScheduleEvent, IScheduleEventAssignee } from '~/utils/interface/schedule';

type CalendarProps = {
  view: View;
  date: Date;
  events: Array<IScheduleEvent>;
};

const cx = classNames.bind(styles);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const Calendar = (props: CalendarProps): ReactElement => {
  const [date, setDate] = useState<Date>(dayjs().toDate());
  const [view, setView] = useState<View>(Views.MONTH);
  const [events, setEvents] = useState<Array<Event>>();

  useEffect(() => {
    setDate(props.date);
  }, [props.date]);

  useEffect(() => {
    if (props.view === Views.MONTH) {
      setEvents(prepareEventsData(props.events, 'date'));
    } else if (props.view === Views.DAY || props.view === Views.WEEK) {
      setEvents(prepareEventsData(props.events, 'hour'));
    }
    setView(props.view);
  }, [props.view, props.events]);

  const prepareEventsData = (events: Array<IScheduleEvent>, groupType: OpUnitType): Array<Event> => {
    const monthEvents: Array<Event> = [];
    for (let event of events) {
      let monthEvent = monthEvents.find((monthEvent: Event) => {
        return dayjs(monthEvent.start).isSame(dayjs(event.start_date), groupType);
      });

      if (!monthEvent) {
        monthEvent = {
          start: event.start_date,
          end: event.end_date,
          resource: [],
        };
        monthEvents.push(monthEvent);
      }

      monthEvent.resource.push(event);
    }

    return monthEvents;
  };

  /**
   * Render month event component
   * @param event
   * @param numberOfEvents
   * @returns
   */
  const renderMonthEventComponent = (event: IScheduleEvent, numberOfEvents: number): ReactElement => {
    const eventColor: string = event.color || EVENT_COLOR_DEFAULT;

    if (numberOfEvents > 1)
      return (
        <Box className={`event-container event-container-title-type bg-[${eventColor}]`}>
          <Box className='text-ellipsis overflow-hidden'>{event.name}</Box>
        </Box>
      );

    return (
      <Box className={`event-container event-container-detailed-type bg-[${eventColor}]`}>
        <Box className='text-ellipsis overflow-hidden'>{event.name}</Box>
        <Box className={cx('event-time')}>
          {dayjs(event.start_date).format('hh:mm a')} - {dayjs(event.end_date).format('hh:mm a')}
        </Box>
        {renderAssigneesEventComponent(event.assignees, eventColor)}
      </Box>
    );
  };

  /**
   * Render assignees Event component
   * @param assignees
   * @returns
   */
  const renderAssigneesEventComponent = (assignees: Array<IScheduleEventAssignee> | undefined, eventColor: string) => {
    if (!assignees) return null;
    const numberOfMoreAssignees: number = assignees.length > 3 ? assignees.length - 3 : 0;
    const displayedAssignees: Array<IScheduleEventAssignee> = assignees.slice(0, 3);

    return (
      <Box className={cx('event-assignees-container')}>
        {displayedAssignees.map((assignee: IScheduleEventAssignee, index: number) => {
          return (
            <Box key={index} className='event-assignee-container'>
              <Box className={`event-assignee border-[2px] border-[${eventColor}]`}>
                <BaseImage src={assignee.image_path} />
              </Box>
            </Box>
          );
        })}
        {numberOfMoreAssignees > 0 && (
          <Box className='event-assignee-container'>
            <Box className={`event-assignee event-assignee-more border-[2px] border-[${eventColor}]`}>
              +{numberOfMoreAssignees}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderDayEventComponent = (event: IScheduleEvent, count: number): ReactElement => {
    const eventColor: string = event.color || EVENT_COLOR_DEFAULT;
    return (
      <Box
        className={cx(
          `bg-[${eventColor}] event-container event-container-detailed-type flex items-center justify-between`,
          { 'flex-wrap !py-0': count >= 5 }
        )}
      >
        <Box className='truncate text-xs mr-2'>{event.name}</Box>
        <Box className={cx('truncate event-time')}>
          {dayjs(event.start_date).format('hh:mm a')} - {dayjs(event.end_date).format('hh:mm a')}
        </Box>
        {count < 5 && renderAssigneesEventComponent(event.assignees, eventColor)}
      </Box>
    );
  };

  return (
    <Box className={cx('calendar-component')}>
      <Box
        className={cx(
          `hour-${date.getHours() + 1}`,
          { 'h-[866px]': view === Views.MONTH },
          { 'calendar-day-view': view === Views.DAY },
          { 'calendar-week-view': view === Views.WEEK }
        )}
      >
        {events && (
          <BaseCalendar
            events={events}
            selectable={true}
            onSelectSlot={() => {}}
            onSelectEvent={() => {}}
            toolbar={false}
            date={date}
            views={views}
            view={view}
            components={{
              event: ({ event }: EventProps) => {
                if (view === Views.MONTH) {
                  return (
                    <BaseScrollbar maxHeight='95px'>
                      <Box>
                        <Box className='flex flex-col gap-[4px]'>
                          {event.resource.map((eventData: IScheduleEvent, index: number) => {
                            return (
                              <React.Fragment key={index}>
                                {renderMonthEventComponent(eventData, event.resource.length)}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    </BaseScrollbar>
                  );
                } else if (view === Views.DAY) {
                  return (
                    <Box
                      className={cx(`event-at-${dayjs(event.start).get('hour')}-hour flex items-center`, {
                        'py-0': event.resource.length >= 5,
                      })}
                    >
                      {event.resource.map((eventData: IScheduleEvent, index: number) => {
                        return (
                          <React.Fragment key={index}>
                            {renderDayEventComponent(eventData, event.resource.length)}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  );
                } else if (view === Views.WEEK) {
                  return (
                    <BaseScrollbar maxHeight='60px' className={cx(`event-at-${dayjs(event.start).get('hour')}-hour`)}>
                      <Box className={cx(`flex flex-col pl-[6px] pr-[6px] gap-[4px]`)}>
                        {event.resource.map((eventData: IScheduleEvent, index: number) => {
                          return (
                            <React.Fragment key={index}>
                              {renderDayEventComponent(eventData, event.resource.length)}
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    </BaseScrollbar>
                  );
                }
                return null;
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default Calendar;
